import React, { useState, useEffect } from "react";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Typography,
} from "@mui/material";
import CoreInputField from "../../../app/core/components/CoreInputField/CoreInputField";
import CoreButton from "../../../app/core/components/CoreButton/CoreButton";
import CoreSelectObjArr from "../../../app/core/components/CoreSelect/CoreSelectObjArr";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import CoreAutoComplete from "../../../app/core/components/CoreAutoComplete/CoreAutoComplete";
import {
  useDoEditProfileMutation,
  useGetCitiesQuery,
} from "../../api/user/modificaProfiloApi";
import { t } from "i18next";
import { actions as appActions } from "../../../app/appSlice";
import { extractYYYYMMDDFromISO } from "../../../utils/functions/date-utils";

const FormUser = ({ user, employment }) => {
  const dispatch = useDispatch();
  const studioId = useSelector((state) => state.app.studioId);
  const appConfig = useSelector((state) => state.app.appConfig);
  const requireUser = appConfig?.modules?.profile;

  const [check, setCheck] = useState("");
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    email: "",
    cap: "",
    common: { cadastralCode: "", name: "", province: "" },
    fiscalCode: "",
    address: "",
    phoneNumber: "",
    employmentStatusId: "",
    userCompany: "",
    gender: "",
    maritalStatus: "",
    birthDate: "",
    birthPlace: { cadastralCode: "", name: "", province: "" },
    iban: "",
    nationality: "",
  });

  const employmentOptions = employment?.map((el) => ({
    label: el?.name,
    value: el?.id,
  }));

  const [doEditProfile, { isLoading }] = useDoEditProfileMutation();

  useEffect(() => {
    if (user) {
      setFormData({
        name: user?.name,
        surname: user?.surname,
        email: user?.email,
        common: {
          cadastralCode: user?.common?.cadastralCode,
          name: user?.common?.name,
          province: user?.common?.province,
        },
        cap: user?.cap,
        fiscalCode: user?.fiscalCode,
        address: user?.address,
        employmentStatusId: user?.employmentStatusId,
        userCompany: user?.userCompany?.name,
        phoneNumber: user?.phoneNumber,
        gender: user?.gender,
        maritalStatus: user?.maritalStatus,
        birthDate: extractYYYYMMDDFromISO(user?.birthDate),
        birthPlace: {
          cadastralCode: user?.birthPlace?.cadastralCode,
          name: user?.birthPlace?.name,
          province: user?.birthPlace?.province,
        },
        iban: user?.iban,
        nationality: user?.nationality,
      });

      setCheck(user.isDichCongiunta);
    }
  }, [user, appConfig]);

  useEffect(() => {
    if (user) {
      const newErrors = {};
      Object.keys(requireUser).forEach((key) => {
        if (requireUser[key] && !user[key]) {
          newErrors[key] = t("FieldRequired");
        }
      });
      setErrors((prev) => ({ ...prev, ...newErrors }));
    }
  }, [user, requireUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setErrors((prevData) => ({ ...prevData, [name]: "" }));
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleInputChangeAuto = (event, newValue) => {
    setErrors((prevData) => ({ ...prevData, comune: "" }));
    setFormData((prevData) => ({
      ...prevData,
      common: {
        cadastralCode: newValue ? newValue?.cadastralCode : "",
        name: newValue ? newValue?.name : "",
      },
    }));
  };

  const handleInputChangebirthPlace = (event, newValue) => {
    setErrors((prevData) => ({ ...prevData, birthPlace: "" }));
    setFormData((prevData) => ({
      ...prevData,
      birthPlace: {
        cadastralCode: newValue ? newValue?.cadastralCode : "",
        name: newValue ? newValue?.name : "",
      },
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = t("FieldRequired");
    if (!formData.surname) newErrors.surname = t("FieldRequired");

    Object.keys(requireUser || {}).forEach((key) => {
      if (requireUser[key] && !formData[key]) {
        newErrors[key] = t("FieldRequired");
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEditProfile = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const bodyDataEditProfile = {
      id: user.id,
      studioId: studioId || null,
      address: formData.address,
      cap: formData.cap,
      fiscalCode: formData.fiscalCode,
      employmentStatusId: formData.employmentStatusId,
      name: formData.name,
      surname: formData.surname,
      email: formData.email,
      isDichCongiunta: check,
      thirdParty: true,
      phoneNumber: formData.phoneNumber,
      nationality: formData.nationality,
      gender: formData.gender,
      iban: formData.iban,
      birthDate: formData.birthDate,
    };

    if (formData.common.cadastralCode)
      bodyDataEditProfile.common = {
        name: formData.common.name,
        cadastralCode: formData.common.cadastralCode,
      };

    if (formData.birthPlace.cadastralCode) {
      bodyDataEditProfile.birthPlace = {
        name: formData.birthPlace.name,
        cadastralCode: formData.birthPlace.cadastralCode,
      };
    }

    doEditProfile(bodyDataEditProfile).then((res) => {
      if (res?.data?.Result !== 1) {
        toast.error(res?.data?.Exception);
        return;
      }
      const profileData = Object.keys(requireUser).reduce((acc, key) => {
        if (requireUser[key]) {
          acc[key] = res.data.Data?.[key];
        }
        return acc;
      }, {});
      dispatch(appActions.setProfileData(profileData));
      toast.success(`${t("Profile")} ${t("edited")}`);
    });
  };

  return (
    <div className="flex flex-col w-full">
      <FormControl>
        <div className="flex space-x-2">
          <CoreSelectObjArr
            error={errors.employmentStatusId}
            label={t("Role")}
            name="employmentStatusId"
            items={employmentOptions}
            onChange={handleInputChange}
            value={formData.employmentStatusId}
            helperText={errors.employmentStatusId}
          />
          {requireUser?.gender && (
            <CoreSelectObjArr
              required
              error={errors.gender}
              label={t("Gender")}
              name="gender"
              items={[
                { label: "Maschio", value: "Male" },
                { label: "Femmina", value: "Female" },
              ]}
              onChange={handleInputChange}
              value={formData.gender}
              helperText={errors.gender}
            />
          )}
        </div>
        {appConfig?.modules?.company?.general && (
          <CoreInputField
            disabled
            className="mt-6"
            label={t("AssociatedCompany")}
            value={formData.userCompany || "N/A"}
          />
        )}

        <CoreInputField
          required
          error={errors.name}
          className="mt-6"
          label={t("Name")}
          name="name"
          onChange={handleInputChange}
          value={formData.name}
          helperText={errors.name}
        />

        <CoreInputField
          required
          error={errors.surname}
          className="mt-6"
          label={t("Surname")}
          name="surname"
          value={formData.surname}
          onChange={handleInputChange}
          helperText={errors.surname}
        />
        {requireUser?.nationality && (
          <CoreInputField
            required
            error={errors.nationality}
            className="mt-6"
            label={t("Nationality")}
            name="nationality"
            value={formData.nationality}
            onChange={handleInputChange}
            helperText={errors.nationality}
          />
        )}

        <div className="flex space-x-4">
          {requireUser?.birthPlace && (
            <div className="w-full mt-8">
              <CoreAutoComplete
                name="birthPlace"
                required={requireUser?.birthPlace}
                error={errors.birthPlace}
                common={formData?.birthPlace?.name}
                label={t("BirthPlace")}
                onChange={handleInputChangebirthPlace}
                fetchData={useGetCitiesQuery}
              />
              {errors.birthPlace && (
                <Typography variant="caption" color="error">
                  {t("FieldRequired")}
                </Typography>
              )}
            </div>
          )}

          {requireUser?.birthDate && (
            <CoreInputField
              type="date"
              required
              error={errors.birthDate}
              className="w-1/3 mt-6"
              label={t("BirthDate")}
              name="birthDate"
              value={formData.birthDate}
              onChange={handleInputChange}
              helperText={errors.birthDate}
            />
          )}
        </div>

        <div className="flex space-x-4">
          <div className="w-full mt-8">
            <CoreAutoComplete
              name="common"
              required={requireUser?.common}
              error={errors.common}
              common={formData.common.name}
              label={t("City")}
              onChange={handleInputChangeAuto}
              fetchData={useGetCitiesQuery}
            />
            {errors.common && (
              <Typography variant="caption" color="error">
                {t("FieldRequired")}
              </Typography>
            )}
          </div>

          <CoreInputField
            error={errors.cap}
            className="mt-6"
            label={t("PostalCode")}
            name="cap"
            value={formData.cap}
            onChange={handleInputChange}
            helperText={errors.cap}
          />
        </div>

        <CoreInputField
          error={errors.address}
          className="mt-6"
          label={t("Address")}
          name="address"
          value={formData.address}
          onChange={handleInputChange}
          helperText={errors.address}
        />

        <div className="flex space-x-4">
          {requireUser?.maritalStatus && (
            <CoreSelectObjArr
              required
              className="mt-8 w-1/3"
              error={errors.maritalStatus}
              label={t("MaritalStatus")}
              name="maritalStatus"
              items={[
                { label: "Single", value: "Single" },
                { label: t("Married"), value: "Married" },
              ]}
              onChange={handleInputChange}
              value={formData.maritalStatus}
              helperText={errors.maritalStatus}
            />
          )}
          <CoreInputField
            required={requireUser?.fiscalCode}
            error={errors.fiscalCode}
            className="mt-6 w-2/3"
            label={t("CF")}
            name="fiscalCode"
            value={formData.fiscalCode}
            onChange={handleInputChange}
            helperText={errors.fiscalCode}
          />

          <CoreInputField
            required={requireUser?.phoneNumber}
            error={errors.phoneNumber}
            className="mt-6 w-1/3"
            label={t("Phone")}
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleInputChange}
            helperText={errors.phoneNumber}
          />
        </div>

        <div className="w-full flex gap-1 items-end space-x-2">
          <CoreInputField
            disabled
            className="mt-6 w-full"
            label="Email"
            type="email"
            name="email"
            value={formData.email}
          />
          <CoreInputField
            disabled
            className="mt-6 w-full"
            label={t("Operator")}
            name="operator"
            value={user?.commDisplayName}
          />
        </div>
        {requireUser?.iban && (
          <CoreInputField
            required
            error={errors.iban}
            className="mt-6"
            label={t("IBAN")}
            name="iban"
            value={formData.iban}
            onChange={handleInputChange}
            helperText={errors.iban}
          />
        )}

        {appConfig.modules.family && (
          <div className="flex justify-between mt-10">
            <h3 className="m-0">Dichiarazione congiunta?</h3>
            <div className="flex">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={check === true}
                    onChange={() => setCheck(true)}
                  />
                }
                label="Si"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={check === false}
                    onChange={() => setCheck(false)}
                  />
                }
                label="No"
              />
            </div>
          </div>
        )}

        <div className="flex justify-around mt-12">
          <CoreButton
            disabled={isLoading}
            sx={{ width: "150px", textAlign: "center" }}
            className={"btn-round"}
            variant="contained"
            testo={t("Save")}
            onClick={handleEditProfile}
          />
        </div>
      </FormControl>
    </div>
  );
};

export default FormUser;
